import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const movementConceptKeys = {
  all: ['movementConcepts'],
  lists: () => [...movementConceptKeys.all, 'list'],
  details: () => [...movementConceptKeys.all, 'detail'],
  detail: (id) => [...movementConceptKeys.details(), id],
};

export const useMovementConcepts = (options = {}) => {
  return useQuery({
    queryKey: movementConceptKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('movement_concepts');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    ...options,
  });
};

export const useMovementConcept = (id, options = {}) => {
  return useQuery({
    queryKey: movementConceptKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`movement_concepts/${id}`);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateMovementConcept = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (movementConceptData) => {
      const response = await apiClient.post('movement_concepts', movementConceptData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.all });
    },
    onError: (error) => {
      console.error('Error creating movement concept:', error);
    },
  });
};

export const useUpdateMovementConcept = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`movement_concepts/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(movementConceptKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.all });
    },
    onError: (error) => {
      console.error('Error updating movement concept:', error);
    },
  });
};

export const useDeleteMovementConcept = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`movement_concepts/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementConceptKeys.all });
      queryClient.removeQueries({ queryKey: movementConceptKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting movement concept:', error);
    },
  });
};
